import moment from 'moment/moment';

/**
 * Fahrzeugtyp der Reise + Km/über/Fahrzeit je Fahrzeug und Tageszeit aus dem Stamm-Routenstichpunkt
 * (predefinedRouteDetail.vehicleRoutes). Gegenstück im Backend:
 * PredefinedRouteDetail::getVehicleRoute() und RouteDetail::applyVehicleRoute().
 */

export const DEFAULT_VEHICLE = 'bus';

export const VEHICLES = [
    { key: 'bus', value: 'Bus' },
    { key: 'car', value: 'Auto' },
    { key: 'minibus', value: 'Minibus' },
];

export const SLOTS = [
    { key: 'morning', value: 'morgens', durationKey: 'durationMorning' },
    { key: 'noon', value: 'mittags', durationKey: 'durationNoon' },
    { key: 'evening', value: 'abends', durationKey: 'durationEvening' },
];

const ROUTE_KEYS = ['distance', 'via', 'durationMorning', 'durationNoon', 'durationEvening'];

/** vehicleRoutes mit allen Fahrzeugen/Keys (für reaktive Formulare), vorhandene Werte bleiben erhalten */
export function emptyVehicleRoutes (routes = null) {
    const result = {};
    VEHICLES.forEach(({ key }) => {
        result[key] = {};
        ROUTE_KEYS.forEach(field => {
            result[key][field] = (routes && routes[key] && routes[key][field]) || null;
        });
    });
    return result;
}

const DATE_FORMAT = 'DD.MM.YYYY HH:mm:ss';

/** morgens < 12:00, mittags 12:00–17:59, abends ab 18:00 */
export function slotFor (startAt, format = DATE_FORMAT) {
    const hour = moment(startAt, format).hour();
    if (hour < 12) {
        return 'morning';
    }
    return hour < 18 ? 'noon' : 'evening';
}

const isFilled = value => value !== null && value !== undefined && String(value).trim() !== '';

/**
 * Feldweise Fallbacks: Dauer der Tageszeit -> Dauer morgens, Fahrzeug -> Bus.
 * @return {{distance: ?string, via: ?string, duration: ?string}}
 */
export function resolveVehicleRoute (predefined, vehicleType, slot = 'morning') {
    const routes = (predefined && predefined.vehicleRoutes) || {};
    const vehicles = [...new Set([vehicleType || DEFAULT_VEHICLE, DEFAULT_VEHICLE])];
    const slotKey = (SLOTS.find(s => s.key === slot) || SLOTS[0]).durationKey;
    const durationKeys = [...new Set([slotKey, 'durationMorning'])];

    const pick = keys => {
        for (const vehicle of vehicles) {
            for (const key of keys) {
                const value = routes[vehicle] && routes[vehicle][key];
                if (isFilled(value)) {
                    return String(value);
                }
            }
        }
        return null;
    };

    return {
        distance: pick(['distance']),
        via: pick(['via']),
        duration: pick(durationKeys),
    };
}

export function durationToSeconds (duration) {
    if (!isFilled(duration)) {
        return null;
    }
    const [hours, minutes, seconds] = String(duration).split(':').map(Number);
    return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
}

/**
 * Setzt distance/via/duration und endAt (= startAt + Dauer) am Reise-Routenstichpunkt.
 * Nur für Fahrten mit Stammbezug und Startzeit; gibt die verwendete Tageszeit zurück (oder null).
 */
export function applyVehicleRoute (placeholder, vehicleType) {
    const predefined = placeholder && placeholder.predefinedRouteDetail;
    if (!predefined || predefined.type !== 'drive' || !placeholder.startAt) {
        return null;
    }
    const slot = slotFor(placeholder.startAt);
    const values = resolveVehicleRoute(predefined, vehicleType, slot);
    placeholder.distance = values.distance;
    placeholder.via = values.via;
    placeholder.duration = values.duration;

    const seconds = durationToSeconds(values.duration);
    if (seconds !== null) {
        placeholder.endAt = moment(placeholder.startAt, DATE_FORMAT).add(seconds, 'seconds').format(DATE_FORMAT);
    }
    return slot;
}

/** "135 km · über A · 2:10 h" */
export function formatVehicleRoute ({ distance, via, duration } = {}) {
    const parts = [];
    if (isFilled(distance)) {
        parts.push(distance + ' km');
    }
    if (isFilled(via)) {
        parts.push('über ' + via);
    }
    if (isFilled(duration)) {
        const [h, m] = String(duration).split(':');
        parts.push(Number(h) + ':' + (m || '00') + ' h');
    }
    return parts.join(' · ');
}

export function vehicleLabel (key) {
    return (VEHICLES.find(v => v.key === key) || VEHICLES[0]).value;
}

export function slotLabel (key) {
    return (SLOTS.find(s => s.key === key) || SLOTS[0]).value;
}
